import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { ChevronLeft, ChevronRightTwoTone } from "@mui/icons-material";
import { Button, IconButton, Stack, Typography } from "@mui/material";
import dayjs, { Dayjs } from "dayjs";

import { useDatetime } from "../../../utils/useDatetime";
import { useArray } from "../../../utils/useArray";
import { DataSkeleton, NoDataWarning } from "../../../components/common";
import { ConfirmAppointmentDialog } from "./creatappointmentdialog/ConfirmAppointmentDialog";
import { isMobile } from "react-device-detect";

interface IProps {
  slots: fhir5.BundleEntry<fhir5.Slot>[] | undefined;
  loading: boolean;
  location: fhir5.Location;
  scheduleCompleted: fhir5.BundleEntry<fhir5.Schedule>[] | undefined;
  setRefetchSlots: Dispatch<SetStateAction<boolean>>;
}

const DAYS_PER_PAGE = isMobile ? 3 : 4;

export function DisponibilityTab({
  slots,
  loading,
  location,
  scheduleCompleted,
  setRefetchSlots,
}: IProps) {
  const [isConfirmAppointmentOpen, setIsConfirmAppointmentOpen] =
    useState<boolean>(false);

  const [scheduleId, setScheduleId] = useState("");
  const [selectedSlot, setSelectedSlot] = useState<fhir5.Slot>();

  const [page, setPage] = useState(0);
  // Estado para controlar a data selecionada pelo usuário
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  
  const { uniqByReduce } = useArray;
  const { getWeekDayTranslation, formatToDDMM, formatToHHmm } = useDatetime;

  const filteredSlots: fhir5.BundleEntry<fhir5.Slot>[] | undefined =
    slots?.filter(
      (el) =>
        el.resource?.start &&
        dayjs(el.resource.start).isAfter(dayjs(), "minute")
    );

  const startDate = filteredSlots?.map((el: fhir5.BundleEntry<fhir5.Slot>) => {
    return el.resource?.start.split("T")?.[0];
  });

  const dates = startDate && uniqByReduce(startDate);

  // Reseta a página e a data selecionada quando os slots mudam
  useEffect(() => {
    setPage(0);
    setSelectedDate(null);
  }, [slots]);

  // Auto-seleciona a primeira data disponível quando as datas são carregadas
  useEffect(() => {
    if (dates && dates.length > 0 && !selectedDate) {
      const sortedDates = dates.sort((a, b) => {
        let dateA = dayjs(a);
        let dateB = dayjs(b);
        return dateA.isAfter(dateB) ? 1 : -1;
      });
      const firstDate = sortedDates[0];
      if (firstDate) {
        setSelectedDate(firstDate);
      }
    }
  }, [dates, selectedDate]);

  return (
    <>
      <Stack
        sx={{ paddingBottom: "20px", paddingTop: "8px" }}
        spacing={2}
        width="100%"
        direction="column"
        alignItems="center"
        justifyContent="flex-start"
      >
        {dates !== undefined && dates.length !== 0 ? (
          <Stack
            width="100%"
            spacing={1.5}
            sx={{
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* Seletor de datas com arrows - layout horizontal mais compacto */}
            <Stack
              direction="row"
              spacing={1}
              sx={{
                justifyContent: "center",
                alignItems: "center",
                flexWrap: "nowrap",
              }}
            >
              {/* Arrow esquerdo - mais próximo das datas */}
              <IconButton
                size="small"
                onClick={() => setPage((prev) => prev - DAYS_PER_PAGE)}
                disabled={
                  dates === undefined ||
                  dates?.length === 0 ||
                  page - DAYS_PER_PAGE < 0
                }
                sx={{ padding: "4px" }}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>

              {/* Container das datas */}
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  maxWidth: "fit-content",
                }}
              >
                {dates
                  .sort((a, b) => {
                    let dateA = dayjs(a);
                    let dateB = dayjs(b);
                    return dateA.isAfter(dateB) ? 1 : -1;
                  })
                  .slice(page, page + DAYS_PER_PAGE)
                  .map((el) => {
                    const date: Dayjs = dayjs(el);
                    const isSelected = selectedDate === el;
                    return (
                      <Button
                        key={`date-selector-${el}`}
                        onClick={() => el && setSelectedDate(el)}
                        variant={isSelected ? "contained" : "outlined"}
                        sx={{
                          minWidth: "85px",
                          height: "50px",
                          display: "flex",
                          flexDirection: "column",
                          padding: "4px 8px",
                          backgroundColor: isSelected ? "primary.main" : "transparent",
                          color: isSelected ? "white" : "text.primary",
                          borderColor: isSelected ? "primary.main" : "divider",
                          "&:hover": {
                            backgroundColor: isSelected ? "primary.dark" : "action.hover",
                          },
                        }}
                      >
                        <Typography variant="caption" fontSize="10px" lineHeight={1}>
                          {getWeekDayTranslation(date)}
                        </Typography>
                        <Typography variant="body2" fontSize="13px" fontWeight="bold" lineHeight={1.2}>
                          {date.format("DD/MM")}
                        </Typography>
                      </Button>
                    );
                  })}
              </Stack>

              {/* Arrow direito - mais próximo das datas */}
              <IconButton
                size="small"
                onClick={() => setPage((prev) => prev + DAYS_PER_PAGE)}
                disabled={
                  dates === undefined ||
                  dates?.length === 0 ||
                  (!!dates?.length && page + DAYS_PER_PAGE >= dates?.length)
                }
                sx={{ padding: "4px" }}
              >
                <ChevronRightTwoTone fontSize="small" />
              </IconButton>
            </Stack>

            {/* Horários disponíveis para a data selecionada - centralizados no topo */}
            {selectedDate && (
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  justifyContent: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  marginTop: 1,
                  width: "100%",
                }}
              >
                {filteredSlots
                  ?.filter(
                    (el) =>
                      el.resource?.start &&
                      dayjs(el.resource.start).isSame(dayjs(selectedDate), "day")
                  )
                  .sort((a, b) => {
                    let dateA = dayjs(a.resource?.start);
                    let dateB = dayjs(b.resource?.start);
                    return dateA.isAfter(dateB) ? 1 : -1;
                  })
                  .map((el) => (
                    <Button
                      key={el?.resource?.id}
                      onClick={() => {
                        setIsConfirmAppointmentOpen(true);
                        setScheduleId(
                          el?.resource?.schedule?.reference?.split("/")[1] || ""
                        );
                        setSelectedSlot(el.resource);
                      }}
                      sx={{
                        backgroundColor: "primary300.main",
                        color: "black",
                        minWidth: "70px",
                        height: "36px",
                        "&:hover": {
                          backgroundColor: "primary200.main",
                        },
                        borderRadius: "8px",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }}
                    >
                      {formatToHHmm(dayjs(el.resource?.start))}
                    </Button>
                  ))}
              </Stack>
            )}
          </Stack>
        ) : loading ? (
          <DataSkeleton />
        ) : (
          <NoDataWarning />
        )}
      </Stack>
      {isConfirmAppointmentOpen && (
        <ConfirmAppointmentDialog
          isOpen={isConfirmAppointmentOpen}
          setIsOpen={setIsConfirmAppointmentOpen}
          location={location}
          scheduleId={scheduleId}
          slot={selectedSlot}
          scheduleCompleted={scheduleCompleted}
          setRefetchSlots={setRefetchSlots}
        />
      )}
    </>
  );
}
